import { ChevronDown, Users } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Iter 137 — multi-team filter shared by the Who's-in slide-out and the full
// In/Out board. Empty selection means "all teams".
export const TeamFilter = ({ teams, value, onChange, countFor, allLabel = "All teams", clearLabel = "Everyone", testid = "team-filter", modal = false }) => {
  const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  const label = value.length === 0 ? allLabel
    : value.length === 1 ? value[0]
    : `${value.length} teams`;
  return (
    // `modal` is required when this sits inside a Dialog: the dialog's
    // scroll-lock swallows wheel events aimed at anything outside its own DOM,
    // and this popover is portalled to the body — so the team list silently
    // refused to scroll and the last team was unreachable.
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="max-w-[130px] truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1" data-testid={`${testid}-menu`}>
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Teams</span>
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])} data-testid={`${testid}-clear`}
              className="text-[11px] font-medium text-primary hover:underline">Clear</button>
          )}
        </div>
        <div className="max-h-72 overflow-auto">
          {/* Iter 144 — explicit "Everyone" row (clears the selection, and also
              brings back people who have no team at all). */}
          <button type="button" onClick={() => onChange([])}
            data-testid={`${testid}-everyone`} data-selected={value.length === 0 ? "1" : "0"}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted">
            <Checkbox checked={value.length === 0} className="pointer-events-none data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
            <span className="min-w-0 flex-1 truncate font-medium">{clearLabel}</span>
          </button>
          <div className="my-1 border-t border-border" />
          {teams.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">No teams set yet.</p>}
          {teams.map((t) => (
            <button key={t} type="button" onClick={() => toggle(t)}
              data-testid={`${testid}-${slug(t)}`} data-selected={value.includes(t) ? "1" : "0"}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted">
              <Checkbox checked={value.includes(t)} className="pointer-events-none data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
              <span className="min-w-0 flex-1 truncate">{t}</span>
              {countFor && <span className="shrink-0 text-[11px] text-muted-foreground">{countFor(t)}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
